import { formatDateTimeFull, formatDateTime } from "@/lib/format";
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import type { AdminRole } from "@/hooks/useAdminAuth";
import { api } from "@/lib/api";
import { normalizeAdminRole } from "@/lib/roles";

interface admin_detail {
  id: string;
  email: string;
  role: AdminRole;
  is_active: boolean;
  totp_verified: boolean;
  failed_otp_count: number;
  locked_until: string | null;
  last_login_at: string | null;
  last_login_ip: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

interface AccountApiDetail {
  id: string;
  email: string;
  username?: string | null;
  role: string;
  is_active: boolean;
  created_at?: string | null;
  last_login_at?: string | null;
}

const roleLabels: Record<AdminRole, string> = {
  super_admin: "超级管理员",
  admin: "管理员",
  customer_support: "客服",
};

const ACCOUNTS_ENDPOINT = "/quantum/business/accounts";

const backendRole: Record<AdminRole, "SUPER_ADMIN" | "OPERATOR" | "CS"> = {
  super_admin: "SUPER_ADMIN",
  admin: "OPERATOR",
  customer_support: "CS",
};

function toAdminDetail(row: AccountApiDetail): admin_detail {
  const createdAt = row.created_at ?? "";
  return {
    id: row.id,
    email: row.email,
    role: normalizeAdminRole(row.role) ?? "admin",
    is_active: row.is_active,
    totp_verified: false,
    failed_otp_count: 0,
    locked_until: null,
    last_login_at: row.last_login_at ?? null,
    last_login_ip: null,
    created_by: null,
    created_at: createdAt,
    updated_at: createdAt,
  };
}

export default function AdminUserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [admin, setAdmin] = useState<admin_detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingRole, setEditingRole] = useState(false);
  const [newRole, setNewRole] = useState<AdminRole>("admin");
  const [editingPassword, setEditingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  const fetchData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await api.get(`${ACCOUNTS_ENDPOINT}/${id}`);
      const data = response.data?.data ? toAdminDetail(response.data.data) : null;
      setAdmin(data);
      if (data) setNewRole(data.role);
    } catch (err) {
      console.error("Admin detail error:", err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleStatus = async () => {
    if (!admin) return;
    await api.put(`${ACCOUNTS_ENDPOINT}/${admin.id}`, { is_active: !admin.is_active });
    fetchData();
  };

  const updateRole = async () => {
    if (!admin) return;
    await api.put(`${ACCOUNTS_ENDPOINT}/${admin.id}`, { role: backendRole[newRole] });
    setEditingRole(false);
    fetchData();
  };

  const resetPassword = async () => {
    if (!admin || !newPassword.trim()) {
      setPasswordError("请输入新密码");
      return;
    }
    try {
      await api.put(`${ACCOUNTS_ENDPOINT}/${admin.id}`, { password: newPassword });
      setEditingPassword(false);
      setNewPassword("");
      setShowPassword(false);
      setPasswordError("");
    } catch {
      setPasswordError("重置失败，请重试");
    }
  };

  const unlock = async () => {
    if (!admin) return;
    await api.put(`${ACCOUNTS_ENDPOINT}/${admin.id}`, { is_active: true });
    fetchData();
  };

  const is_locked = admin?.locked_until && new Date(admin.locked_until) > new Date();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <i className="ri-loader-4-line animate-spin text-3xl text-brand" />
      </div>
    );
  }

  if (!admin) {
    return (
      <div className="text-center py-20">
        <i className="ri-admin-line text-4xl text-text-muted mb-3" />
        <p className="text-text-secondary">管理员不存在或已被删除</p>
        <button onClick={() => navigate("/admin-users")} className="mt-4 text-brand text-sm hover:underline cursor-pointer">
          返回管理员列表
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/admin-users")}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-surface text-text-secondary transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
          </button>
          <h2 className="text-xl font-semibold text-text-primary">管理员详情</h2>
        </div>
        <div className="flex items-center gap-2">
          {is_locked && (
            <button
              onClick={unlock}
              className="px-4 py-2 rounded-lg bg-status-warning/10 text-status-warning text-sm font-medium hover:bg-status-warning/20 transition-colors cursor-pointer"
            >
              <i className="ri-lock-unlock-line mr-1" />
              解锁账号
            </button>
          )}
          <button
            onClick={toggleStatus}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
              admin.is_active
                ? "bg-status-danger/10 text-status-danger hover:bg-status-danger/20"
                : "bg-status-success/10 text-status-success hover:bg-status-success/20"
            }`}
          >
            {admin.is_active ? "禁用账号" : "启用账号"}
          </button>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-white rounded-xl border border-surface-border p-5">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-full bg-brand/10 flex items-center justify-center text-brand font-semibold text-lg">
            {admin.email[0].toUpperCase()}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-1">
              <h3 className="text-lg font-semibold text-text-primary">{admin.email}</h3>
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                admin.is_active ? "bg-status-success/10 text-status-success" : "bg-text-muted/10 text-text-muted"
              }`}>
                {admin.is_active ? "正常" : "已禁用"}
              </span>
              {is_locked && (
                <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-status-danger/10 text-status-danger">
                  <i className="ri-lock-line mr-1" /> 已锁定
                </span>
              )}
            </div>
            <div className="flex items-center gap-6 mt-3 text-xs text-text-muted">
              <span>角色：{roleLabels[admin.role]}</span>
              <span>2FA：{admin.totp_verified ? "已绑定" : "未绑定"}</span>
              <span>登录失败：{admin.failed_otp_count}/5</span>
            </div>
          </div>
        </div>
      </div>

      {/* Detail Sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-surface-border p-5">
          <h4 className="text-sm font-medium text-text-primary mb-3">账号信息</h4>
          <div className="space-y-2.5 text-sm">
            <div className="flex justify-between"><span className="text-text-muted">管理员ID</span><span className="text-text-primary font-mono text-xs">{admin.id}</span></div>
            <div className="flex justify-between"><span className="text-text-muted">邮箱</span><span className="text-text-primary">{admin.email}</span></div>
            <div className="flex justify-between items-center">
              <span className="text-text-muted">角色</span>
              <div className="flex items-center gap-2">
                {editingRole ? (
                  <>
                    <select
                      value={newRole}
                      onChange={(e) => setNewRole(e.target.value as AdminRole)}
                      className="h-7 px-2 rounded-md bg-surface text-xs text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none"
                    >
                      <option value="super_admin">超级管理员</option>
                      <option value="admin">管理员</option>
                      <option value="customer_support">客服</option>
                    </select>
                    <button onClick={updateRole} className="text-brand text-xs hover:underline cursor-pointer">保存</button>
                    <button onClick={() => setEditingRole(false)} className="text-text-muted text-xs hover:underline cursor-pointer">取消</button>
                  </>
                ) : (
                  <>
                    <span className="text-text-primary">{roleLabels[admin.role]}</span>
                    <button onClick={() => setEditingRole(true)} className="text-brand text-xs hover:underline cursor-pointer">修改</button>
                  </>
                )}
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-text-muted">密码</span>
              <div className="flex items-center gap-2">
                {editingPassword ? (
                  <>
                    {passwordError && <span className="text-status-danger text-xs">{passwordError}</span>}
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") resetPassword(); }}
                        placeholder="新密码"
                        className="h-7 px-2 pr-8 rounded-md bg-surface text-xs text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none w-40"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary transition-colors"
                        tabIndex={-1}
                      >
                        <i className={`${showPassword ? "ri-eye-off-line" : "ri-eye-line"} text-xs`} />
                      </button>
                    </div>
                    <button onClick={resetPassword} className="text-brand text-xs hover:underline cursor-pointer">保存</button>
                    <button
                      onClick={() => { setEditingPassword(false); setNewPassword(""); setShowPassword(false); setPasswordError(""); }}
                      className="text-text-muted text-xs hover:underline cursor-pointer"
                    >取消</button>
                  </>
                ) : (
                  <>
                    <span className="text-text-muted tracking-widest text-xs">••••••••</span>
                    <button onClick={() => setEditingPassword(true)} className="text-brand text-xs hover:underline cursor-pointer">重置</button>
                  </>
                )}
              </div>
            </div>
            <div className="flex justify-between"><span className="text-text-muted">TOTP验证</span><span className={admin.totp_verified ? "text-status-success" : "text-text-muted"}>{admin.totp_verified ? "已验证" : "未验证"}</span></div>
            <div className="flex justify-between"><span className="text-text-muted">创建时间</span><span className="text-text-muted text-xs">{formatDateTimeFull(admin.created_at)}</span></div>
            <div className="flex justify-between"><span className="text-text-muted">更新时间</span><span className="text-text-muted text-xs">{formatDateTimeFull(admin.updated_at)}</span></div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-surface-border p-5">
          <h4 className="text-sm font-medium text-text-primary mb-3">登录信息</h4>
          <div className="space-y-2.5 text-sm">
            <div className="flex justify-between"><span className="text-text-muted">最后登录</span><span className="text-text-primary text-xs">{admin.last_login_at ? formatDateTime(admin.last_login_at) : "从未登录"}</span></div>
            <div className="flex justify-between"><span className="text-text-muted">最后IP</span><span className="text-text-primary font-mono text-xs">{admin.last_login_ip || "-"}</span></div>
            <div className="flex justify-between"><span className="text-text-muted">失败次数</span><span className={admin.failed_otp_count >= 3 ? "text-status-danger" : "text-text-primary"}>{admin.failed_otp_count}/5</span></div>
            <div className="flex justify-between">
              <span className="text-text-muted">锁定状态</span>
              <span className={is_locked ? "text-status-danger" : "text-status-success"}>
                {is_locked ? `锁定至 ${formatDateTime(admin.locked_until!)}` : "未锁定"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
